/**
 * MultiTenantScoreComparison Component
 *
 * A dashboard widget for MSPs to compare Guardian Scores across all managed tenants.
 * Uses parallel data fetching via useQueries to efficiently load scores for multiple tenants.
 *
 * Security considerations:
 * - Only renders for global admins with multiple tenants
 * - Iterates exclusively over userTenants from the authenticated context
 * - Each API call includes proper tenant authorization
 * - No cross-tenant data access is possible
 *
 * @example
 * ```tsx
 * // Renders automatically for eligible users, returns null otherwise
 * <MultiTenantScoreComparison />
 * ```
 */

import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useTenantContext } from '@/hooks/useTenantContext'
import { calculateUnifiedGuardianScore } from '@/hooks/useComplianceData'
import { BuildingOffice2Icon } from '@heroicons/react/24/outline'

interface TenantScore {
  tenantId: string
  tenantName: string
  guardianScore: number | null
  secureScore: number | null
  bpaScore: number | null
  mfaAdoption: number | null
}

/**
 * Multi-Tenant Score Comparison for MSPs.
 *
 * This component:
 * - Only shows for global admins with multiple tenant access
 * - Fetches scores in parallel for all accessible tenants
 * - Displays a ranked list with average score
 * - Ensures tenant isolation by only accessing userTenants from context
 */
export default function MultiTenantScoreComparison() {
  const { userTenants, isGlobalAdmin } = useTenantContext()

  // Do not render for non-admins or single-tenant scenarios
  // This is a critical security check - only iterate over authorized tenants
  const shouldRender = isGlobalAdmin && userTenants.length > 1

  // Build query configurations for each tenant the user has access to
  // IMPORTANT: We only fetch data for tenants in userTenants, which come
  // from the authenticated user's context. This ensures tenant isolation.
  const queryConfigs = useMemo(() => {
    if (!shouldRender) return []

    return userTenants.map((tenant) => ({
      queryKey: ['multi-tenant-score', tenant.tenantId],
      queryFn: async () => {
        try {
          // Fetch data for this specific tenant
          // Each API call is authenticated and authorized for this tenant
          const [secureScore, bpaData, userInsights, dashboardStats] = await Promise.all([
            api.getSecureScoreSummary(tenant.tenantId).catch(() => null),
            api.getComplianceScores(tenant.tenantId).catch(() => null),
            api.getUserInsights(tenant.tenantId).catch(() => null),
            api.getDashboardStats(tenant.tenantId).catch(() => null),
          ])

          const secureScoreValue = secureScore?.score?.percentage ?? null
          const bpaScoreValue = bpaData?.overallScore ?? null
          const mfaValue = userInsights?.mfaMemberAdoptionPercentage ?? null
          const configValue = dashboardStats?.complianceScore?.percentage ?? null

          return {
            tenantId: tenant.tenantId,
            tenantName: tenant.displayName || tenant.name || tenant.tenantId,
            secureScore: secureScoreValue,
            bpaScore: bpaScoreValue,
            mfaAdoption: mfaValue,
            guardianScore: calculateUnifiedGuardianScore({
              secureScore: secureScoreValue,
              bpaScore: bpaScoreValue,
              mfaAdoption: mfaValue,
              configCompliance: configValue,
            }),
          }
        } catch {
          // Return partial data on error
          return {
            tenantId: tenant.tenantId,
            tenantName: tenant.displayName || tenant.name || tenant.tenantId,
            guardianScore: null,
            secureScore: null,
            bpaScore: null,
            mfaAdoption: null,
          }
        }
      },
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      enabled: shouldRender,
    }))
  }, [userTenants, shouldRender])

  // Execute all queries in parallel using useQueries
  const results = useQueries({
    queries: queryConfigs,
    combine: (queryResults) => {
      const loadedData: TenantScore[] = []
      for (const result of queryResults) {
        if (result.data !== undefined) {
          loadedData.push(result.data as TenantScore)
        }
      }
      return {
        data: loadedData,
        isPending: queryResults.some((result) => result.isPending),
        isError: queryResults.some((result) => result.isError),
      }
    },
  })

  // Sort tenants by Guardian Score (highest first)
  const sortedTenants = useMemo((): TenantScore[] => {
    return [...results.data].sort((a, b) => (b.guardianScore ?? 0) - (a.guardianScore ?? 0))
  }, [results.data])

  // Calculate average score across all tenants with valid scores
  const averageScore = useMemo((): number | null => {
    const validScores = results.data.filter((t) => t.guardianScore != null)
    if (validScores.length === 0) return null
    const sum = validScores.reduce((acc, t) => acc + (t.guardianScore ?? 0), 0)
    return Math.round(sum / validScores.length)
  }, [results.data])

  // Do not render if conditions are not met
  if (!shouldRender) {
    return null
  }

  // Get color class based on score value
  const getScoreColor = (score: number | null): string => {
    if (score == null) return 'text-slate-500'
    if (score >= 80) return 'text-emerald-400'
    if (score >= 60) return 'text-amber-400'
    return 'text-red-400'
  }

  // Render loading skeleton
  if (results.isPending) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700 p-6 animate-pulse">
        <div className="h-6 bg-dark-700 rounded w-48 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-dark-700 rounded" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-dark-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <BuildingOffice2Icon className="h-5 w-5 text-primary-400" />
            Multi-Tenant Comparison
          </h3>
          <p className="text-sm text-slate-400 mt-1">
            Guardian Scores across {sortedTenants.length} managed tenant{sortedTenants.length !== 1 ? 's' : ''}
          </p>
        </div>
        {averageScore != null && (
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Average</div>
            <div className={`text-2xl font-bold ${getScoreColor(averageScore)}`}>
              {averageScore}%
            </div>
          </div>
        )}
      </div>

      {/* Tenant List */}
      <div className="divide-y divide-dark-700">
        {sortedTenants.map((tenant, index) => (
          <div
            key={tenant.tenantId}
            className="px-6 py-4 flex items-center justify-between hover:bg-dark-700/30 transition-colors"
          >
            <div className="flex items-center gap-4">
              {/* Rank indicator */}
              <div className="w-8 h-8 rounded-lg bg-dark-700 flex items-center justify-center text-sm font-medium text-slate-400">
                {index + 1}
              </div>
              <div>
                <div className="font-medium text-slate-200">{tenant.tenantName}</div>
                <div className="text-xs text-slate-500">
                  Secure: {tenant.secureScore?.toFixed(0) ?? '-'}%
                  {' | '}
                  BPA: {tenant.bpaScore?.toFixed(0) ?? '-'}%
                  {' | '}
                  MFA: {tenant.mfaAdoption?.toFixed(0) ?? '-'}%
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Guardian Score */}
              <div className={`text-xl font-bold ${getScoreColor(tenant.guardianScore)}`}>
                {tenant.guardianScore != null ? `${tenant.guardianScore}%` : '-'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {sortedTenants.length === 0 && !results.isPending && (
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-slate-400">No tenant data available</p>
        </div>
      )}
    </div>
  )
}
